import { QuestionType } from '../../types';

export type QuestionHelperCopy = {
  general?: string;
  fields?: Partial<Record<'text' | 'header' | 'subtext' | 'low_text' | 'high_text' | 'explanation', string>>;
  metadata?: string;
  options?: {
    general?: string;
    front?: string;
    back?: string;
    value?: string;
  };
};

const baseOptionReminders = {
  value: 'Optional numeric value for scoring/ordering. Leave as-is unless required.',
};

const voiceMetadataReminder =
  'Voice-mode authoring keys: "voice_follow_up_hints" (array of short follow-up cues) and "voice_transition_reflection" (one-time transition line used when the conversation moves to the next topic).';

export const QUESTION_FIELD_HELP_TEXT: Partial<Record<QuestionType, QuestionHelperCopy>> = {
  rating: {
    fields: {
      low_text: 'Low end of the scale (e.g., "Strongly Disagree").',
      high_text: 'High end of the scale (e.g., "Strongly Agree").',
    },
    metadata: voiceMetadataReminder,
    options: {
      general: 'List scale labels or leave empty if the numeric scale is sufficient.',
      value: baseOptionReminders.value,
    },
  },
  boolean: {
    fields: {
      low_text: 'Display text for the "false" option (e.g., "No" or "False").',
      high_text: 'Display text for the "true" option (e.g., "Yes" or "True").',
    },
    metadata:
      `${voiceMetadataReminder} Supported key: "explanation" (shown after answering). For quiz answer keys, author Correct Option Indexes instead of metadata. "correct_value" remains legacy boolean compatibility only.`,
  },
  multiple_reveal: {
    general: 'Use reveal cards when you want learners to flip through paired front/back content.',
    options: {
      general: 'Enter each card\'s front/back copy. Markdown is supported; existing source stays unchanged. Learners click to reveal the back.',
      front: 'Front text supports inline emphasis and text sizes.',
      back: 'Back text supports paragraphs, lists, headings and text sizes. Links are read-only labels; citation markers stay literal.',
      value: baseOptionReminders.value,
    },
  },
  horizontal_drag_drop: {
    general:
      'Provide draggable items that learners sort into left or right buckets. Low/High text labels the columns.',
    fields: {
      low_text: 'Title for the left column.',
      high_text: 'Title for the right column.',
    },
    options: {
      general:
        'Each option belongs to a column. Set back_text to "left" or "right" to assign the correct column.',
      front: 'Display text shown on the draggable item.',
      back: 'Column assignment: must be "left" or "right".',
      value: baseOptionReminders.value,
    },
  },
  accordion: {
    general: 'Accordion questions display expandable rows; each option becomes one accordion item.',
    fields: {
      text: 'Introductory copy shown above the accordion.',
      subtext: 'Optional supporting text beneath the title.',
    },
    options: {
      general: 'Each option creates one accordion row with title + description.',
      front: 'Accordion title shown in the collapsed state.',
      back: 'Accordion description revealed when expanded.',
      value: baseOptionReminders.value,
    },
  },
  additional_resources: {
    general: 'Shows a simple list of resource cards. Each option becomes one linked resource.',
    fields: {
      text: 'Question text is forced to "Additional Resources" during import.',
      header: 'Header is cleared automatically on import.',
      subtext: 'Subtext is cleared automatically on import.',
    },
    options: {
      general: 'Front text becomes the resource title; back text should be the URL.',
      front: 'Resource title displayed to learners.',
      back: 'Resource link (include https:// or it will be added automatically).',
      value: baseOptionReminders.value,
    },
  },
  multiple_choice: {
    metadata:
      'Supported key: "explanation" (shown after answering). Use Correct Option Indexes as the canonical answer key for quizzes.',
    options: {
      general: 'Enter the answer choices in order. Option 1 is the first choice shown to learners.',
      front: 'Choice text displayed to the learner.',
      value: baseOptionReminders.value,
    },
  },
  multiple_select: {
    metadata:
      'Supported keys: "min_selections" and "max_selections" to require a minimum or maximum number of selected options.',
    options: {
      general: 'Enter each selectable choice. Use the checkboxes to allow "Other" when needed.',
      front: 'Choice text displayed to the learner.',
      value: baseOptionReminders.value,
    },
  },
  drag_and_drop: {
    options: {
      general: 'Provide the list of items learners should reorder.',
      front: 'Item label shown in the draggable list.',
      value: baseOptionReminders.value,
    },
  },
  drag_and_drop_ranking: {
    options: {
      general: 'List the items to rank; learners will drag them into the desired order.',
      front: 'Item label shown in the draggable list.',
      value: baseOptionReminders.value,
    },
  },
  text_list: {
    metadata:
      `${voiceMetadataReminder} Supported keys: "text_list_length" (number of input rows), "text_list_defaults" (array of default values).`,
  },
  text_or_audio: {
    metadata: voiceMetadataReminder,
  },
  description: {
    fields: {
      low_text:
        'Optional fill-in-the-blank or supplemental text displayed beneath the description.',
    },
  },
  callback_summary: {
    metadata:
      'Required key: "custom_user_prompt" (nonblank, with {key} placeholders for response references). Optional key: "custom_system_prompt". Existing legacy "template" prompts remain supported.',
  },
  ai_summary: {
    general: 'Automatically gathers all previous responses from the module and generates an AI summary. No user input required.',
    metadata:
      'Optional keys: "custom_system_prompt" (override the default summarization prompt), "custom_user_prompt" (prepended to the gathered responses).',
  },
  roleplay: {
    general:
      'A roleplay question can be one step in a standard module; its module cannot be a quiz or use standard Module Voice. Configure the private actor and win prompts below.',
    fields: {
      header: 'Learner-facing roleplay title.',
      text: 'The learner-facing situation shown before practice begins.',
      subtext: 'Optional learner-facing subtitle.',
      low_text: 'The learner’s role in this situation.',
      high_text: 'Optional exact opening line. Leave blank to generate a fresh opening.',
    },
    metadata:
      'Roleplay metadata stores the persona key, learning objectives, private actor and win-condition prompts, starting expression, voice, duration, and turn limit.',
  },
};

export const getQuestionHelperCopy = (type: QuestionType): QuestionHelperCopy | undefined =>
  QUESTION_FIELD_HELP_TEXT[type];
